import { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
  Bell,
  CheckCheck,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Info,
} from "lucide-react";
import {
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type Notification,
  type NotificationSeverity,
} from "@/lib/api";
import { formatRelative } from "@/lib/format";

type Props = {
  open: boolean;
  onClose: () => void;
};

export function NotificationsDropdown({ open, onClose }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const panelRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["notifications", "recent"],
    queryFn: () => listNotifications({ limit: 10 }),
    enabled: open,
    staleTime: 15_000,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const markAll = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open, onClose]);

  if (!open) return null;

  const items = data?.data ?? [];
  const unreadCount = items.filter((n) => n.read_at === null).length;

  const handleClick = (n: Notification) => {
    if (n.read_at === null) markRead.mutate(n.id);
    onClose();
    if (n.action_url) navigate(n.action_url);
  };

  return (
    <div
      ref={panelRef}
      className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-dropdown z-30 border border-[#eff2f5] overflow-hidden"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#eff2f5]">
        <div className="flex items-center gap-2">
          <Bell size={16} className="text-[#3f4254]" />
          <div className="font-bold text-[#181c32] text-sm">Notifications</div>
          {unreadCount > 0 && (
            <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-brand-50 text-brand-700">
              {unreadCount} non lues
            </span>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            onClick={() => markAll.mutate()}
            disabled={markAll.isPending}
            className="text-xs text-brand-700 hover:underline font-medium inline-flex items-center gap-1"
          >
            <CheckCheck size={12} />
            Tout marquer lu
          </button>
        )}
      </div>

      <div className="max-h-[420px] overflow-y-auto">
        {isLoading && (
          <div className="px-4 py-8 text-center text-sm text-[#7e8299]">Chargement…</div>
        )}
        {!isLoading && items.length === 0 && (
          <div className="px-4 py-10 text-center">
            <Bell size={28} className="mx-auto text-[#cbd5e0] mb-2" />
            <div className="text-sm text-[#7e8299]">Aucune notification pour l'instant.</div>
          </div>
        )}
        {items.map((n) => (
          <button
            key={n.id}
            onClick={() => handleClick(n)}
            className={`w-full text-left px-4 py-3 border-b border-[#f5f8fa] hover:bg-[#f9fafb] transition-colors flex items-start gap-3 ${
              n.read_at === null ? "bg-brand-50/30" : ""
            }`}
          >
            <div className="shrink-0 mt-0.5">
              <SeverityIcon severity={n.severity} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-2">
                <div
                  className={`text-sm text-[#181c32] truncate ${
                    n.read_at === null ? "font-bold" : "font-medium"
                  }`}
                >
                  {n.title}
                </div>
                <div className="text-[10px] text-[#a1a5b7] shrink-0">
                  {formatRelative(n.created_at)}
                </div>
              </div>
              <div className="text-xs text-[#5e6278] leading-relaxed mt-0.5 line-clamp-2">
                {n.body}
              </div>
            </div>
            {n.read_at === null && (
              <div className="w-2 h-2 rounded-full bg-brand-500 shrink-0 mt-2" />
            )}
          </button>
        ))}
      </div>

      <div className="px-4 py-2.5 border-t border-[#eff2f5] bg-[#fcfcfd]">
        <Link
          to="/notifications"
          onClick={onClose}
          className="text-xs text-brand-700 hover:underline font-medium"
        >
          Voir toutes les notifications →
        </Link>
      </div>
    </div>
  );
}

function SeverityIcon({ severity }: { severity: NotificationSeverity }) {
  const size = 18;
  switch (severity) {
    case "success":
      return <CheckCircle2 size={size} className="text-success-700" />;
    case "warning":
      return <AlertTriangle size={size} className="text-warning-700" />;
    case "danger":
      return <XCircle size={size} className="text-danger-700" />;
    case "info":
    default:
      return <Info size={size} className="text-info-700" />;
  }
}
